import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { GalleryProvider, useGallery } from '../context/GalleryContext';
import api from '../apiClient';
import { guestApiPath, setStoredGalleryId } from '../api';
import LoadingSpinner from './LoadingSpinner';
import GalleryNotFound from './GalleryNotFound';
import SessionVerifyBanner from './SessionVerifyBanner';
import PolicyLinks from '../legal/PolicyLinks';
import {
  GUEST_AUTHENTICATED_EVENT,
  clearStoredGuestAuthFailure,
  getLocalGuestTokenFailureReason,
  getStoredGuestAuthFailure,
  invalidateGuestSession,
  recoverGuestApiAuthFailure,
} from '../utils/guestAuthRedirect';
import { isInIframe, createMessageHandler, getAllowedOrigins } from '../utils/iframeCommunication';
import { loadLogo, saveTheme, saveLogo, DEFAULT_FONT } from '../utils/themeStorage';
import { applyTheme } from '../utils/themeUtils';
import {
  clearPreviewSessionToken,
  discardStoredPreviewSession,
  getRestorablePreviewToken,
  getStoredGuestToken,
  hasPreviewSessionToken,
  setPreviewSessionToken,
} from '../utils/guestToken';

function GalleryLayoutBody() {
  const { galleryId: routeGalleryId = '' } = useParams();
  const { pathname } = useLocation();
  const {
    loading,
    error,
    publicConfig,
    logoUrl,
    galleryId: resolvedGalleryId,
    previewToken,
    previewRequested,
    refetchPublicConfig,
  } = useGallery();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCheckDone, setAuthCheckDone] = useState(false);
  // Why an authorized-looking preview session cannot be used: 'ended' (the server refused the
  // preview token) or 'retry' (the check itself failed, e.g. offline or a 5xx). '' while healthy.
  const [previewFailure, setPreviewFailure] = useState('');
  const [sessionCheckAttempt, setSessionCheckAttempt] = useState(0);
  const [authWarmUpDegraded, setAuthWarmUpDegraded] = useState(false);
  const [authFailure, setAuthFailure] = useState(() =>
    getStoredGuestAuthFailure(routeGalleryId)
  );
  const inIframe = isInIframe();
  // The editor preview iframe offered a preview token but the session cannot be a preview: it must
  // report the preview as unavailable. It never degrades into the couple's own guest session or the
  // invite-code screen (a reloaded preview frame has lost its in-memory token).
  const previewBlocksGuest = inIframe && previewRequested && !previewToken;

  useEffect(() => {
    setAuthFailure(getStoredGuestAuthFailure(routeGalleryId));
  }, [routeGalleryId]);

  useEffect(() => {
    if (inIframe && !sessionStorage.getItem('wedding_theme_config')) {
      const publishedTheme = localStorage.getItem('wedding_theme_config');
      const publishedLogo = localStorage.getItem('wedding_logo_config');
      const publishedTabs = localStorage.getItem('wedding_tabs_config');
      if (publishedTheme) {
        sessionStorage.setItem('wedding_theme_config', publishedTheme);
      }
      if (publishedLogo) {
        sessionStorage.setItem('wedding_logo_config', publishedLogo);
      }
      if (publishedTabs) {
        sessionStorage.setItem('wedding_tabs_config', publishedTabs);
      }
    }
  }, [inIframe]);

  // The preview token lives in memory for this gallery route (never in weddingToken) and, for reloads
  // of the preview iframe, in a tab-scoped sessionStorage mirror that outlives this component.
  useEffect(() => clearPreviewSessionToken, []);

  useEffect(() => {
    let cleanup;
    if (inIframe) {
      const handleUpdateTheme = (data) => {
        try {
          if (!data || !data.colors) {
            return;
          }
          const colors = data.colors;
          const font = data.font || DEFAULT_FONT;
          applyTheme({ colors, font });
          saveTheme(colors, font);
        } catch (e) {
          console.error('GalleryLayout: theme message error', e);
        }
      };
      const handleUpdateLogo = (data) => {
        try {
          if (!data) {
            return;
          }
          const currentLogoData = loadLogo();
          const logo = data.logo !== undefined ? data.logo : currentLogoData.logo;
          const text = data.text !== undefined ? data.text : currentLogoData.text;
          saveLogo(logo, text);
        } catch (e) {
          console.error('GalleryLayout: logo message error', e);
        }
      };
      cleanup = createMessageHandler(handleUpdateTheme, handleUpdateLogo, () => {}, () => {}, getAllowedOrigins());
    }
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [inIframe]);

  useEffect(() => {
    if (!previewToken) {
      clearPreviewSessionToken();
    }
    if (loading) {
      return;
    }
    if (error) {
      setAuthCheckDone(true);
      return;
    }
    const gid = resolvedGalleryId;
    if (!gid) {
      setAuthCheckDone(true);
      return;
    }
    if (publicConfig?.status !== 'active' && !publicConfig?.previewAuthorized) {
      setIsAuthenticated(false);
      setAuthCheckDone(true);
      return;
    }
    if (previewBlocksGuest) {
      setIsAuthenticated(false);
      setAuthCheckDone(true);
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (!cancelled) {
        setPreviewFailure('');
      }
      // A preview token left in weddingToken by earlier builds is ignored and cleared here.
      const token = previewToken || getStoredGuestToken();
      if (!token) {
        if (!cancelled) {
          setIsAuthenticated(false);
          setAuthCheckDone(true);
        }
        return;
      }
      const localFailureReason = previewToken
        ? null
        : getLocalGuestTokenFailureReason(token, gid);
      if (localFailureReason) {
        const detail = invalidateGuestSession({
          galleryId: routeGalleryId || gid,
          reason: localFailureReason,
        });
        if (!cancelled) {
          setAuthFailure(detail);
          setIsAuthenticated(false);
          setAuthWarmUpDegraded(false);
          setAuthCheckDone(true);
        }
        return;
      }
      try {
        await api.get(guestApiPath('/photos', gid), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          if (previewToken) {
            setPreviewSessionToken(previewToken, routeGalleryId || gid);
            setStoredGalleryId(gid);
            // Uploads queued before the preview token was known wait for this, as for an invite login.
            window.dispatchEvent(
              new CustomEvent(GUEST_AUTHENTICATED_EVENT, { detail: { galleryId: gid } })
            );
          }
          clearStoredGuestAuthFailure();
          setAuthFailure(null);
          setIsAuthenticated(true);
          setAuthWarmUpDegraded(false);
        }
      } catch (e) {
        const detail = recoverGuestApiAuthFailure(e, routeGalleryId || gid);
        if (previewToken && inIframe) {
          console.error('Preview session check failed:', e);
          if (!cancelled) {
            const status = e?.response?.status;
            setPreviewFailure(detail || status === 401 || status === 403 ? 'ended' : 'retry');
            setIsAuthenticated(false);
            setAuthWarmUpDegraded(false);
          }
        } else if (detail) {
          if (!cancelled) {
            setAuthFailure(detail);
            setIsAuthenticated(false);
            setAuthWarmUpDegraded(false);
          }
        } else {
          console.error('Token validation failed:', e);
          if (!cancelled) {
            const hasStoredGuestToken =
              !previewToken && Boolean(getStoredGuestToken());
            setIsAuthenticated(hasStoredGuestToken);
            setAuthWarmUpDegraded(hasStoredGuestToken);
          }
        }
      }
      if (!cancelled) {
        setAuthCheckDone(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    loading,
    error,
    previewToken,
    previewBlocksGuest,
    inIframe,
    sessionCheckAttempt,
    publicConfig?.previewAuthorized,
    publicConfig?.status,
    resolvedGalleryId,
    routeGalleryId,
  ]);

  const retrySessionCheck = () => {
    window.location.reload();
  };

  const retryPreviewCheck = () => {
    setPreviewFailure('');
    setAuthCheckDone(false);
    setSessionCheckAttempt((attempt) => attempt + 1);
  };

  const clearGuestAuthFailure = useCallback(() => {
    setAuthFailure(null);
  }, []);

  useEffect(() => {
    const handleAuthError = (event) => {
      const eventGalleryId =
        typeof event.detail?.galleryId === 'string' ? event.detail.galleryId.trim() : '';
      if (
        !eventGalleryId ||
        (eventGalleryId !== routeGalleryId && eventGalleryId !== resolvedGalleryId)
      ) {
        return;
      }
      if (inIframe && (previewToken || hasPreviewSessionToken())) {
        // The preview token expired while the frame was open. The invite-code screen cannot recover
        // a preview (the preview token keeps winning), so report the preview as ended instead.
        setPreviewFailure('ended');
        setIsAuthenticated(false);
        return;
      }
      setAuthFailure(event.detail || getStoredGuestAuthFailure(routeGalleryId));
      setIsAuthenticated(false);
    };
    window.addEventListener('auth-error', handleAuthError);
    return () => window.removeEventListener('auth-error', handleAuthError);
  }, [inIframe, previewToken, resolvedGalleryId, routeGalleryId]);

  if (loading || !authCheckDone) {
    return <LoadingSpinner logo={logoUrl} />;
  }
  if (error === 'not-found') {
    return <GalleryNotFound />;
  }
  if (error === 'network') {
    return (
      <div role="alert" className="flex flex-col items-center justify-center min-h-screen bg-background p-6 text-center">
        <h1 className="text-xl font-serif text-secondary mb-2">Unable to load gallery</h1>
        <p className="text-secondary-70 text-sm max-w-md">
          Check your connection, then try again.
        </p>
        <button
          type="button"
          onClick={() => {
            setAuthCheckDone(false);
            refetchPublicConfig();
          }}
          className="mt-4 min-h-[44px] rounded-lg bg-primary px-6 py-3 text-surface hover:bg-primary-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          Try again
        </button>
      </div>
    );
  }
  if (publicConfig?.status !== 'active' && !publicConfig?.previewAuthorized) {
    return <GalleryStatusMessage status={publicConfig?.status} goLiveAt={publicConfig?.goLiveAt} />;
  }
  if (previewBlocksGuest || previewFailure === 'ended') {
    return <PreviewUnavailableMessage />;
  }
  if (previewFailure === 'retry') {
    return <PreviewUnavailableMessage onRetry={retryPreviewCheck} />;
  }

  return (
    <>
      {authWarmUpDegraded ? (
        <SessionVerifyBanner onRetry={retrySessionCheck} />
      ) : null}
      <Outlet
        context={{
          isAuthenticated,
          setIsAuthenticated,
          clearGuestAuthFailure,
          logoUrl,
          publicConfig,
          authWarmUpDegraded,
          retrySessionCheck,
          authFailure,
        }}
      />
      {!/\/slideshow\/?$/.test(pathname) && (
        <footer className="border-t border-accent-20 bg-surface px-4 py-5 text-center">
          <PolicyLinks />
        </footer>
      )}
    </>
  );
}

function GalleryStatusMessage({ status, goLiveAt }) {
  const scheduledDate = goLiveAt
    ? new Date(goLiveAt).toLocaleString(undefined, {
        dateStyle: 'long',
        timeStyle: 'short',
      })
    : null;
  const messages = {
    draft: ['Gallery not live yet', 'This gallery is still being prepared. Please check back later.'],
    scheduled: [
      'Gallery opening soon',
      scheduledDate
        ? `This gallery is scheduled to open on ${scheduledDate}.`
        : 'This gallery is scheduled to open soon.',
    ],
    archived: ['Gallery archived', 'This gallery is no longer available.'],
    expired: ['Gallery expired', 'This gallery is no longer available.'],
  };
  const [heading, detail] = messages[status] || [
    'Gallery unavailable',
    'This gallery is not currently available.',
  ];
  return (
    <main className="flex flex-col items-center justify-center min-h-screen bg-background p-6 text-center">
      <img src="/placeholder-logo.svg" alt="" className="h-16 w-16 mb-6" />
      <h1 className="text-2xl font-serif text-secondary mb-2">{heading}</h1>
      <p className="text-secondary-70 text-sm max-w-md">{detail}</p>
    </main>
  );
}

/**
 * Shown in the editor preview iframe instead of the guest session or the invite-code screen.
 * Without `onRetry` the preview session is over; with it the session check itself failed
 * (offline, 5xx) and trying again can succeed.
 */
function PreviewUnavailableMessage({ onRetry }) {
  return (
    <main className="flex flex-col items-center justify-center min-h-screen bg-background p-6 text-center">
      <img src="/placeholder-logo.svg" alt="" className="h-16 w-16 mb-6" />
      {onRetry ? (
        <>
          <h1 className="text-2xl font-serif text-secondary mb-2">Unable to load the preview</h1>
          <p className="text-secondary-70 text-sm max-w-md">
            Check your connection, then try again.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 min-h-[44px] rounded-lg bg-primary px-6 py-3 text-surface hover:bg-primary-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            Try again
          </button>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-serif text-secondary mb-2">Preview unavailable</h1>
          <p className="text-secondary-70 text-sm max-w-md">
            This preview session has ended. Reopen the preview from the editor to continue.
          </p>
        </>
      )}
    </main>
  );
}

export default function GalleryLayout() {
  const { galleryId } = useParams();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const urlPreviewToken =
    searchParams.get('previewMode') === 'true' ? searchParams.get('token') || '' : '';
  // A reload of the preview iframe drops both the in-memory token and the bootstrap query. Resume
  // the tab-scoped mirror instead (iframe only, same gallery), so the frame stays a preview.
  const [previewSession, setPreviewSession] = useState(() => ({
    galleryId,
    token: urlPreviewToken || getRestorablePreviewToken(galleryId),
  }));
  const explicitlyExitPreview = searchParams.has('previewMode') && searchParams.get('previewMode') !== 'true';
  const previewToken = explicitlyExitPreview ? '' : urlPreviewToken || (
    previewSession.galleryId === galleryId
      ? previewSession.token
      : getRestorablePreviewToken(galleryId)
  );
  // Nested navigation removes the bootstrap query; retain it only for this mounted gallery route.
  if (previewSession.galleryId !== galleryId || previewSession.token !== previewToken) {
    setPreviewSession({ galleryId, token: previewToken });
  }
  // A top-level tab never resumes a preview, and an explicit exit ends it: drop the mirror.
  useEffect(() => {
    if (explicitlyExitPreview || !isInIframe()) {
      discardStoredPreviewSession();
    }
  }, [explicitlyExitPreview]);
  return (
    <GalleryProvider galleryId={galleryId || ''} previewToken={previewToken}>
      <GalleryLayoutBody />
    </GalleryProvider>
  );
}
